import React, { FC, useContext, useMemo } from "react";

import { css } from "@emotion/react";
import { getTextColor } from "@intelligems/ig-utils";

import { DiscountQuantityContext } from "../context";

interface BottomMessageProps {
  mode: "hover" | "extra";
  quantity: number;
  unitDiscount: number;
  message?: string;
  fullPriceMessage?: string | null;
}

export const ExtraMessage: FC<BottomMessageProps> = ({
  mode,
  message,
  quantity,
  unitDiscount,
  fullPriceMessage,
}) => {
  const { theme, currencyFormatter, windowState, getWidgetConfig } = useContext(
    DiscountQuantityContext
  );

  const {
    save = "Save",
    each = "Each",
    off = "Off",
    percent = "%",
    // @ts-ignore
  } = getWidgetConfig("variables.textReplacements") || {
    save: "Save",
    each: "Each",
    off: "Off",
    percent: "%",
  };

  const extraMessageClass = css`
    text-align: center;
    margin: ${theme.extraMessage.margin};
    border-radius: ${theme.extraMessage.borderRadius};
    width: ${theme.extraMessage.width};

    @media only screen and (max-width: 600px) {
      min-width: ${theme.extraMessage.minWidth};
      height: 18px;
      font-size: 12px;
      line-height: 12px;
      padding: 3px 0 3px 0;
    }
    @media only screen and (min-width: 600px) {
      min-width: ${theme.extraMessage.minWidth};
      height: 24px;
      font-size: 13px;
      line-height: 18px;
      padding: ${theme.extraMessage.padding};
    }
  `;

  const extraMessagePrimaryClass = css`
    background-color: ${theme.colors.primary};
    color: ${getTextColor(
      getComputedStyle(document.documentElement).getPropertyValue(
        theme.colors.primaryKey
      )
    )};
  `;

  const extraMessageSecondaryClass = css`
    background-color: ${theme.colors.secondary};
    color: ${getTextColor(
      getComputedStyle(document.documentElement).getPropertyValue(
        theme.colors.secondaryKey
      )
    )};
  `;

  const extraMessagePrimaryHoverClass = css`
    ${extraMessageClass};
    ${extraMessagePrimaryClass}
  `;

  const extraMessageSecondaryHoverClass = css`
    ${extraMessageClass};
    ${extraMessageSecondaryClass}
  `;

  const hoverMessage = useMemo(() => {
    let totalDiscount;
    if (windowState.discount.discountType === "dollar") {
      totalDiscount = quantity * unitDiscount;
    } else {
      totalDiscount = unitDiscount;
    }

    if (totalDiscount === 0) {
      return fullPriceMessage ?? "Full Price";
    } else {
      return windowState.discount.discountType === "dollar"
        ? `${save} ${currencyFormatter(quantity * unitDiscount)}`
        : `${unitDiscount.toFixed(0)}${percent} ${off}`;
    }
  }, [quantity, unitDiscount, windowState.discount.discountType]);

  if (mode === "hover" && message && message !== "") {
    return (
      <div
        className={"ig-volume-widget-extra-message-hover"}
        css={extraMessageSecondaryHoverClass}
      >
        {hoverMessage}
      </div>
    );
  }

  if (!message || message === "") {
    return (
      <div
        className={"ig-volume-widget-extra-message-empty"}
        css={extraMessageClass}
      >
        {" "}
      </div>
    );
  }

  return (
    <div
      className={"ig-volume-widget-extra-message"}
      css={css`
        ${extraMessageClass};
        ${extraMessagePrimaryClass}
      `}
    >
      {message}
    </div>
  );
};
